import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

import "@26lights/orcha/dist/orcha.css";
import {
  feedFilterTypes,
  AoFeed,
  AddFilesPayload,
  Attachment,
  RemoveFilePayload,
} from "@26lights/orcha";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import enUK from "@/locales/activityFeed/en-UK.json";
import { useI18n } from "vue-i18n";
import {
  UseMutationOptions,
  useMutation,
  useQuery,
  useLazyQuery,
  useApolloClient,
  provideApolloClient,
} from "@vue/apollo-composable";
import {
  GET_ACTIVITY,
  GET_ACTIVITY_BY_SLUG,
  GET_ACTIVITY_STATUSES,
  GET_ACTIVITY_DEV_STATUSES,
  GET_BASIC_ACTIVITIES,
} from "@/graphql/orcha/activities/queries";
import { GET_HISTORY_EVENTS } from "@/graphql/auditConfig/queries";
import { getUser, isEmployee } from "@/helpers/router/queries";
import { User } from "@/helpers/user/typesDefinition";
import { GET_ACTIVITY_TIME_SPENT } from "@/graphql/timesheets/queries";
import {
  GET_CLIENT_PROJECTS,
  GET_EMPLOYEE_PROJECTS,
  GET_PROJECT_USERS,
} from "@/graphql/project/queries";
import { GET_EMPLOYEES } from "@/graphql/user/queries";
import {
  CREATE_ACTIVITY,
  UPDATE_ACTIVITY,
  ARCHIVE_ACTIVITY,
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
  UNARCHIVE_ACTIVITY,
} from "@/graphql/orcha/activities/mutations";
import {
  CREATE_TASK,
  UPDATE_TASK,
  DELETE_TASK,
} from "@/graphql/orcha/tasks/mutations";
import {
  GET_SPRINTS,
  GET_SPRINTS_FOR_PROJECTS,
} from "@/graphql/orcha/sprints/queries";
import { CREATE_SPRINT } from "@/graphql/orcha/sprints/mutations";
import { GET_MILESTONES_FOR_PROJECTS } from "@/graphql/orcha/milestones/queries";
import { GET_PLANNED_MAN_DAYS_FOR_SPRINTS } from "@/graphql/planning/queries";
import useMutationHandlers from "@/composables/useMutationHandlers";
import {
  CREATE_COMMENT,
  UPDATE_COMMENT,
  DELETE_COMMENT,
  ON_NOTIFY_MENTIONED_USERS,
} from "@/graphql/orcha/comments/mutations";
import { getFileBlob, uploadFile } from "@/services/file";
import { useToast } from "vue-toastification";
import usePaginatedQuery from "@/composables/usePaginatedQuery";
import {
  FeedFiltersSearch,
  ProjectAccessRight,
} from "@/helpers/typesDefinition";
import { updateQueryCache } from "@/helpers/cache/cache";
import { insertNewEntity } from "@/helpers/cache/insertNewEntity";
import ActivityTasksCacheInfoHolder from "@/helpers/cache/ActivityTasksCacheInfoHolder";
import { removeEntity } from "@/helpers/cache/removeEntity";
import ActivityCommentsCacheInfoHolder from "@/helpers/cache/ActivityCommentsCacheInfoHolder";
import ActivityAttachmentsCacheInfoHolder from "@/helpers/cache/ActivityAttachmentsCacheInfoHolder";
import { GET_CLIENT_FROM_PROJECT } from "@/graphql/client/queries";
import {
  AddCommentPayload,
  UpdateCommentPayload,
} from "@26lights/orcha/dist/src/types";
import { useRouter } from "vue-router";
import { apolloClient } from "@/apollo.provider";

type MessageSchema = typeof enUK;

interface Props {
  initialActivityId?: string;
  initialActivitySlug?: string;
  /** Optional: filter to show only specific status columns by name (e.g., ["Today"]) */
  statusFilter?: string[];
  /** Hide the filter header (search, multiselects, toggle buttons) */
  hideFilters?: boolean;
  /** Optional: pre-set project filter by project IDs */
  projectFilter?: string[];
  /** Feed mode: 'standard' for normal activities, 'dev' for development activities */
  feedMode?: "standard" | "dev";
}

export default /*@__PURE__*/_defineComponent({
  __name: 'ActivityFeed',
  props: {
    initialActivityId: {},
    initialActivitySlug: {},
    statusFilter: {},
    hideFilters: { type: Boolean },
    projectFilter: {},
    feedMode: {}
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

const toast = useToast();
const router = useRouter();

const user = ref<User>();
const filters = ref<FeedFiltersSearch>({
  searchTerm: "",
  [feedFilterTypes.project]: [],
  [feedFilterTypes.sprint]: [],
  [feedFilterTypes.assignee]: [],
  includesInactive: false,
  onlyImportant: false,
  onlyNonAssigned: false,
});

const { createErrorHandler, createResultHandler } = useMutationHandlers();

const isUserReady = computed(() => Boolean(user.value));

const { result: basicActivitiesResult, onError: onActivitiesError } = useQuery(
  GET_BASIC_ACTIVITIES,
  filters,
  () => ({
    enabled: isUserReady.value,
  }),
);
onActivitiesError(createErrorHandler("getActivities"));

const activities = computed(
  () => basicActivitiesResult.value?.activities ?? [],
);

// Fetch time spent for all displayed activities
const activityIdsForQuery = computed(() => {
  const ids = activities.value.map((a: { id: string }) => a.id).filter(Boolean);
  return ids.length > 0 ? ids : undefined;
});

const activityTimeSpentQueryOptions = computed(() => ({
  enabled: Boolean(activityIdsForQuery.value),
}));

const { result: activityTimeSpentResult } = useQuery(
  GET_ACTIVITY_TIME_SPENT,
  () => ({
    activityIds: activityIdsForQuery.value,
  }),
  activityTimeSpentQueryOptions,
);

const timeTrackingData = computed(() => {
  const result = activityTimeSpentResult.value?.activityTimeSpent?.data ?? [];
  const map: Record<string, { timeSpent: number }> = {};
  result.forEach((item: { activityId: string; timeSpent: number }) => {
    map[item.activityId] = { timeSpent: item.timeSpent };
  });
  return map;
});

const { result: activityStatusesResult, onError: onActivityStatusesError } =
  useQuery(GET_ACTIVITY_STATUSES);
// Full unfiltered list of statuses - passed to AoFeed for activity details dropdown
const allActivityStatuses = computed(() => {
  return activityStatusesResult.value?.activityStatuses ?? [];
});
// Filtered list of statuses - passed to AoFeed as columnStatuses for kanban columns
const activityStatuses = computed(() => {
  const allStatuses = activityStatusesResult.value?.activityStatuses ?? [];
  if (props.statusFilter?.length) {
    return allStatuses.filter((s: { name: string }) =>
      props.statusFilter!.includes(s.name),
    );
  }
  return allStatuses;
});
onActivityStatusesError(createErrorHandler("getActivityStatuses"));

const {
  result: activityDevStatusesResult,
  onError: onActivityDevStatusesError,
} = useQuery(GET_ACTIVITY_DEV_STATUSES);
const activityDevStatuses = computed(() => {
  return activityDevStatusesResult.value?.activityDevStatuses ?? [];
});
onActivityDevStatusesError(createErrorHandler("getActivityDevStatuses"));

const projects = ref([]);

const isUserEmployee = computed(() =>
  user.value ? isEmployee(user.value.accessRights) : false,
);

const { result: employeesResult, onError: onEmployeesError } = useQuery(
  GET_EMPLOYEES,
  {},
  () => ({
    enabled: isUserEmployee.value,
  }),
);
onEmployeesError(createErrorHandler("getEmployees"));

const employees = computed(() => {
  return employeesResult.value?.users ?? [];
});

// Sprint query - loads sprints for selected projects (for top-level filter)
const { result: sprintsResult, refetch: refetchSprints } = useQuery(
  GET_SPRINTS_FOR_PROJECTS,
  () => ({
    projectIds: filters.value.projectId ?? [],
  }),
  () => ({
    enabled: filters.value.projectId && filters.value.projectId.length > 0,
  }),
);

const sprints = computed(() => {
  return sprintsResult.value?.sprintsForProjects ?? [];
});

// Milestones query - loads milestones for selected projects (for top-level filter)
const { result: milestonesResult } = useQuery(
  GET_MILESTONES_FOR_PROJECTS,
  () => ({
    projectIds: filters.value.projectId ?? [],
  }),
  () => ({
    enabled: filters.value.projectId && filters.value.projectId.length > 0,
  }),
);

const milestones = computed(() => {
  return milestonesResult.value?.milestonesForProjects ?? [];
});

// Get selected sprint IDs from filter
const selectedSprintIds = computed(() => {
  return filters.value[feedFilterTypes.sprint] ?? [];
});

// Build sprint configs for planning query
const sprintConfigs = computed(() => {
  return selectedSprintIds.value
    .map((id) => {
      const sprint = sprints.value.find((s: { id: string }) => s.id === id);
      if (!sprint) return null;
      return {
        projectId: sprint.projectId,
        startDate: sprint.startDate,
        endDate: sprint.endDate,
      };
    })
    .filter(Boolean);
});

// Get assignee filter IDs
const assigneeIds = computed(() => {
  return filters.value[feedFilterTypes.assignee] ?? [];
});

// Planning data query - loads planned man-days for selected sprints
const { result: planningResult } = useQuery(
  GET_PLANNED_MAN_DAYS_FOR_SPRINTS,
  () => ({
    sprints: sprintConfigs.value,
    userIds: assigneeIds.value.length > 0 ? assigneeIds.value : undefined,
  }),
  () => ({
    enabled: sprintConfigs.value.length > 0,
  }),
);

// Transform planning result into a map by sprint ID
const sprintPlanningData = computed(() => {
  if (!planningResult.value?.plannedManDaysForSprints) return {};

  const data: Record<
    string,
    {
      plannedManDays: number;
      remainingPlannedManDays: number;
      users: Array<{
        userId: string;
        fullName: string;
        plannedManDays: number;
      }>;
    }
  > = {};

  planningResult.value.plannedManDaysForSprints.forEach(
    (planning: {
      projectId: string;
      startDate: string;
      endDate: string;
      plannedManDays: number;
      remainingPlannedManDays: number;
      users: Array<{
        userId: string;
        fullName: string;
        plannedManDays: number;
      }>;
    }) => {
      // Find the matching sprint
      const matchingSprint = sprints.value.find(
        (s: { projectId: string; startDate: string; endDate: string }) =>
          s.projectId === planning.projectId &&
          s.startDate === planning.startDate &&
          s.endDate === planning.endDate,
      );
      if (matchingSprint) {
        data[matchingSprint.id] = {
          plannedManDays: planning.plannedManDays,
          remainingPlannedManDays: planning.remainingPlannedManDays,
          users: planning.users ?? [],
        };
      }
    },
  );

  return data;
});

const activityId = ref(props.initialActivityId);
const activitySlug = ref(props.initialActivitySlug);

const { result: activityByIdResult } = useQuery(
  GET_ACTIVITY,
  () => ({ id: activityId.value ?? "" }),
  () => ({ enabled: !!activityId.value && !activitySlug.value }),
);

const { result: activityBySlugResult } = useQuery(
  GET_ACTIVITY_BY_SLUG,
  () => ({ slug: activitySlug.value ?? "" }),
  () => ({ enabled: !!activitySlug.value }),
);

const activity = computed(() => {
  if (activitySlug.value) {
    return activityBySlugResult.value?.activityBySlug;
  }
  if (activityId.value) {
    return activityByIdResult.value?.activity;
  }
  return undefined;
});

// History query for selected activity (for Activity Details)
// Lazy-loaded: no request is made until the History tab explicitly asks for it.
// Use the loaded activity ID as a fallback so history also works when the activity
// was initially opened by slug rather than by ID.
const historyRecordId = computed(() => activity.value?.id ?? activityId.value);

const {
  result: activityHistoryResult,
  loading: activityHistoryLoading,
  load: loadHistory,
  refetch: refetchHistory,
} = useLazyQuery(
  GET_HISTORY_EVENTS,
  () => ({
    tableName: "base_activities",
    recordId: historyRecordId.value ?? "",
    depth: 1,
  }),
  {
    fetchPolicy: "network-only",
  },
);

const activityHistory = computed(() => {
  return activityHistoryResult.value?.historyEvents ?? [];
});

// First History opening starts the lazy query; later openings refetch it from
// the network. Passing the variables explicitly on refetch also guarantees that
// switching activities while History is open fetches the newly selected activity.
function fetchHistory() {
  const recordId = historyRecordId.value;
  if (!recordId) return;

  loadHistory() ||
    refetchHistory({
      tableName: "base_activities",
      recordId,
      depth: 1,
    });
}

// Sprint query for selected activity's project (for Activity Details)
const selectedActivityProjectId = computed(() => activity.value?.project?.id);

const { result: activitySprintsResult, refetch: refetchActivitySprints } =
  useQuery(
    GET_SPRINTS,
    () => ({
      projectId: selectedActivityProjectId.value ?? "",
    }),
    () => ({
      enabled: Boolean(selectedActivityProjectId.value),
    }),
  );

const activitySprints = computed(() => {
  return activitySprintsResult.value?.sprints ?? [];
});

watch(isUserEmployee, (newIsUserEmployee) => {
  provideApolloClient(apolloClient);
  let query;
  if (newIsUserEmployee) {
    query = GET_EMPLOYEE_PROJECTS;
  } else {
    query = GET_CLIENT_PROJECTS;
  }
  const {
    paginatedResult: projectsResult,
    onResult: onProjectsResult,
    loadMore: loadMoreProjects,
  } = usePaginatedQuery(query, t("errors.getEmployeeProjects"), { limit: 100 });
  onProjectsResult(() => {
    if (projectsResult.value.hasMore) {
      nextTick().then(loadMoreProjects);
    }
    projects.value = projectsResult.value.items ?? [];
  });
});

onMounted(async () => {
  user.value = await getUser();
  filters.value.assigneeId = [user.value.id];
  // Apply initial project filter if provided
  if (props.projectFilter?.length) {
    filters.value[feedFilterTypes.project] = [...props.projectFilter];
  }
});

// Watch for external project filter changes
watch(
  () => props.projectFilter,
  (newFilter) => {
    if (newFilter) {
      filters.value[feedFilterTypes.project] = [...newFilter];
    }
  },
  { deep: true },
);

const refetchQueries: UseMutationOptions["refetchQueries"] = () => {
  const selectedActivityId = activity.value?.id;

  return [
    "getFullActivities",
    "getBasicActivities",
    ...(selectedActivityId
      ? [
          {
            query: GET_ACTIVITY,
            variables: {
              id: selectedActivityId,
            },
          },
        ]
      : []),
  ];
};

async function buildActivityVariables(payload: object) {
  const projectId = payload.projectId;
  let clientPrefix;
  if (projectId) {
    const apolloClient = useApolloClient().client;
    try {
      const { data } = await apolloClient.query({
        query: GET_CLIENT_FROM_PROJECT,
        variables: { projectId },
      });
      clientPrefix = data?.projectClient?.prefix;
    } catch (error) {
      toast.error("There was an error when retrieving client");
      console.error("Error retrieving client:", error);
    }
  }
  return {
    ...payload,
    ...(clientPrefix ? { slugPrefix: clientPrefix } : {}),
  };
}

const {
  mutate: createActivity,
  onError: onActivityCreatedError,
  onDone: onActivityCreated,
} = useMutation(CREATE_ACTIVITY, { refetchQueries });
onActivityCreatedError(createErrorHandler("createActivity"));
onActivityCreated(createResultHandler("createActivity"));

async function handleCreateActivity(payload: object) {
  const variables = await buildActivityVariables(payload);
  await createActivity(variables);
}

const {
  mutate: updateActivity,
  onError: onActivityUpdatedError,
  onDone: onActivityUpdated,
} = useMutation(UPDATE_ACTIVITY, {
  refetchQueries,
});
onActivityUpdatedError(createErrorHandler("updateActivity"));
onActivityUpdated(createResultHandler("updateActivity"));

async function handleUpdateActivity(payload: object) {
  const variables = await buildActivityVariables(payload);
  await updateActivity(variables);
}

const {
  mutate: createTask,
  onError: onTaskCreatedError,
  onDone: onTaskCreated,
} = useMutation(CREATE_TASK, {
  refetchQueries,
  update: (cache, { data: { createTask } }) => {
    if (createTask?.data) {
      return updateQueryCache(
        cache,
        createTask?.data,
        ActivityTasksCacheInfoHolder,
        insertNewEntity,
        activityId.value as string,
      );
    }
  },
});
onTaskCreatedError(createErrorHandler("createTask"));
onTaskCreated(createResultHandler("createTask"));

const {
  mutate: updateTask,
  onError: onTaskUpdatedError,
  onDone: onTaskUpdated,
} = useMutation(UPDATE_TASK, { refetchQueries });
onTaskUpdatedError(createErrorHandler("updateTask"));
onTaskUpdated(createResultHandler("updateTask"));

const {
  mutate: deleteTask,
  onError: onTaskDeletedError,
  onDone: onTaskDeleted,
} = useMutation(DELETE_TASK, {
  refetchQueries,
  update: (cache, { data: { deleteTask } }) => {
    if (deleteTask?.data) {
      return updateQueryCache(
        cache,
        deleteTask?.data,
        ActivityTasksCacheInfoHolder,
        removeEntity,
        activityId.value as string,
      );
    }
  },
});
onTaskDeletedError(createErrorHandler("deleteTask"));
onTaskDeleted(createResultHandler("deleteTask"));

const {
  mutate: createComment,
  onError: onCommentCreatedError,
  onDone: onCommentCreated,
} = useMutation(CREATE_COMMENT, {
  refetchQueries,
  update: (cache, { data: { createComment } }) => {
    if (createComment?.data) {
      return updateQueryCache(
        cache,
        createComment?.data,
        ActivityCommentsCacheInfoHolder,
        insertNewEntity,
        activityId.value as string,
      );
    }
  },
});
onCommentCreatedError(createErrorHandler("createComment"));
onCommentCreated(createResultHandler("createComment"));

const handleCreateComment = async (payload: AddCommentPayload) => {
  const { activityId, authorId, message, mentionedUserIds } = payload;

  const result = await createComment({
    activityId,
    authorId,
    message,
  });

  if (result?.data?.createComment?.status === "ok") {
    if (mentionedUserIds.length === 0) return;
    const activityRoute = router.resolve({
      name: "ActivityFeed",
      params: {
        id: activityId,
      },
    });
    const fullLink = window.location.origin + activityRoute.href;

    await notifyMentionedUsers({
      userIds: mentionedUserIds,
      comment: result.data.createComment.data.message,
      activityName: activity.value.name,
      activityLink: fullLink,
      authorId,
    });
  }
};

const {
  mutate: notifyMentionedUsers,
  onError: onNotifyMentionedUsersError,
  onDone: onNotifyMentionedUsers,
} = useMutation(ON_NOTIFY_MENTIONED_USERS, {});
onNotifyMentionedUsersError(createErrorHandler("notifyMentionedUsers"));
onNotifyMentionedUsers(createResultHandler("notifyMentionedUsers"));

const {
  mutate: updateComment,
  onError: onCommentUpdatedError,
  onDone: onCommentUpdated,
} = useMutation(UPDATE_COMMENT, {
  refetchQueries,
});
onCommentUpdatedError(createErrorHandler("updateComment"));
onCommentUpdated(createResultHandler("updateComment"));

const handleUpdateComment = async (payload: UpdateCommentPayload) => {
  const { id, message, mentionedUserIds } = payload;

  const result = await updateComment({
    id,
    message,
  });

  if (result?.data?.updateComment?.status === "ok") {
    if (mentionedUserIds.length === 0) return;

    const activityRoute = router.resolve({
      name: "ActivityFeed",
      params: {
        id: activityId.value,
      },
    });
    const fullLink = window.location.origin + activityRoute.href;

    await notifyMentionedUsers({
      userIds: mentionedUserIds,
      comment: result.data.updateComment.data.message,
      activityName: activity.value.name,
      activityLink: fullLink,
      authorId: result.data.updateComment.data.author.id,
    });
  }
};

const {
  mutate: deleteComment,
  onError: onCommentDeletedError,
  onDone: onCommentDeleted,
} = useMutation(DELETE_COMMENT, {
  refetchQueries,
  update: (cache, { data: { deleteComment } }) => {
    if (deleteComment?.data) {
      return updateQueryCache(
        cache,
        deleteComment?.data,
        ActivityCommentsCacheInfoHolder,
        removeEntity,
        activityId.value as string,
      );
    }
  },
});
onCommentDeletedError(createErrorHandler("deleteComment"));
onCommentDeleted(createResultHandler("deleteComment"));

// Create sprint mutation
const { mutate: createSprint, onError: onSprintCreatedError } =
  useMutation(CREATE_SPRINT);
onSprintCreatedError(createErrorHandler("createSprint"));

async function handleCreateSprint(payload: {
  projectId: string;
  label?: string;
  startDate: string;
  endDate: string;
}) {
  const result = await createSprint(payload);
  const sprint = result?.data?.createSprint?.data;

  if (!sprint || !activity.value?.id) return;

  // Refetch activity sprints for the selected activity's project
  await refetchActivitySprints({
    projectId: sprint.projectId,
  });

  // Update the activity with the new sprint
  await updateActivity({
    id: activity.value.id,
    sprintId: sprint.id,
  });

  // Also refetch top-level sprints for the filter
  const projectIds = filters.value.projectId;
  if (projectIds && projectIds.length > 0) {
    await refetchSprints({
      projectIds,
    });
  }

  showToast(t("activity.sprint.created"), "success");
}

const {
  mutate: archiveActivity,
  onError: onActivityArchivedError,
  onDone: onActivityArchived,
} = useMutation(ARCHIVE_ACTIVITY, {
  refetchQueries: () => [
    {
      query: GET_BASIC_ACTIVITIES,
      variables: filters.value,
    },
    {
      query: GET_BASIC_ACTIVITIES,
      variables: {
        ...filters.value,
        includesInactive: !filters.value.includesInactive,
      },
    },
  ],
});
onActivityArchivedError(createErrorHandler("archiveActivity"));
onActivityArchived(createResultHandler("archiveActivity"));

const {
  mutate: unarchiveActivity,
  onError: onActivityUnarchivedError,
  onDone: onActivityUnarchived,
} = useMutation(UNARCHIVE_ACTIVITY, {
  refetchQueries: () => [
    {
      query: GET_BASIC_ACTIVITIES,
      variables: filters.value,
    },
    {
      query: GET_BASIC_ACTIVITIES,
      variables: {
        ...filters.value,
        includesInactive: !filters.value.includesInactive,
      },
    },
  ],
});
onActivityUnarchivedError(createErrorHandler("unarchiveActivity"));
onActivityUnarchived(createResultHandler("unarchiveActivity"));

const isTaskCheckable = () => true;

const { mutate: createAttachment } = useMutation(CREATE_ATTACHMENT, {
  refetchQueries,
  update: (cache, { data: { createAttachment } }) => {
    if (createAttachment?.data) {
      return updateQueryCache(
        cache,
        createAttachment?.data,
        ActivityAttachmentsCacheInfoHolder,
        insertNewEntity,
        activityId.value as string,
      );
    }
  },
});

const addFiles = async ({ activityId, files }: AddFilesPayload) => {
  try {
    const result = await uploadFile(files);
    Promise.all(
      result.map((fileId) => createAttachment({ activityId, fileId })),
    );
  } catch (error) {
    toast.error(t(`errors.uploadingFile`));
  }
};

const loadAttachmentPreview = async (attachment: Attachment): Promise<Blob> => {
  if (!attachment.url) {
    throw new Error("Attachment URL is missing");
  }

  return getFileBlob(attachment.url);
};

const {
  mutate: deleteAttachment,
  onDone: onAttachmentDeleted,
  onError: onAttachmentDeletedError,
} = useMutation(DELETE_ATTACHMENT, {
  refetchQueries,
  update: (cache, { data: { deleteAttachment } }) => {
    if (deleteAttachment?.data) {
      return updateQueryCache(
        cache,
        deleteAttachment?.data,
        ActivityAttachmentsCacheInfoHolder,
        removeEntity,
        activityId.value as string,
      );
    }
  },
});
onAttachmentDeletedError(createErrorHandler("deleteAttachment"));
onAttachmentDeleted(createResultHandler("deleteAttachment"));

const removeFile = (payload: RemoveFilePayload) => {
  deleteAttachment({ id: payload.file.id });
};

const selectActivity = async (
  givenActivityId: string | null,
  givenActivitySlug: string | null,
) => {
  activityId.value = givenActivityId ?? undefined;
  activitySlug.value = givenActivitySlug ?? undefined;
};

const { result: projectUsersResult, onError: onProjectUsersError } = useQuery(
  GET_PROJECT_USERS,
  () => ({
    projectId: selectedActivityProjectId.value || "",
  }),
  () => ({
    enabled: !!selectedActivityProjectId.value,
  }),
);
onProjectUsersError(createErrorHandler("getProjectUsers"));

// When an activity is selected, we retrieve the list of users with employees and clients related to the corresponding project
const selectedActivityUsers = computed(() => {
  return (projectUsersResult.value?.project?.accessRights?.items ?? [])
    .map((ar: ProjectAccessRight) => ar.user)
    .filter((u: User) => u.firstName && u.lastName);
});

function showToast(messageKey: string, type: string) {
  if (type === "success") {
    toast.success(t(messageKey));
  } else {
    toast.error(t(messageKey));
  }
}

// Handle startTimer event from AoFeed - emit to parent via custom window event
// Parameters from AoFeed: activityId, activityName, project (projectName), projectId, activitySlug
const emitStartTimer = (payload: {
  activityId: string;
  activityName: string;
  project?: string;
  projectId?: string;
  activitySlug?: string;
  // Legacy fields for backward compatibility
  name?: string;
  slug?: string;
}) => {
  // Support both new format (activityId, activityName, activitySlug) and legacy (name, slug)
  const activityId = payload.activityId;
  const activityName = payload.activityName || payload.name || "";
  const projectId = payload.projectId;
  const project = payload.project;
  const activitySlug = payload.activitySlug || payload.slug;
  const note = activitySlug ? `${activitySlug}: ${activityName}` : activityName;

  console.log("[ActivityFeed] emitStartTimer called with:", {
    activityId,
    activityName,
    projectId,
    project,
    activitySlug,
    note,
  });

  // Dispatch custom event with full payload for FloatingTimer
  window.dispatchEvent(
    new CustomEvent("fides:startTimer", {
      detail: {
        activityId,
        activityName,
        projectId,
        project,
        activitySlug,
      },
    }),
  );
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass({ 'activity-feed--hide-filters': __props.hideFilters })
  }, [
    (user.value)
      ? (_openBlock(), _createBlock(_unref(AoFeed), {
          key: 0,
          "search-text": filters.value.searchTerm,
          "onUpdate:searchText": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.searchTerm) = $event)),
          filters: filters.value,
          "onUpdate:filters": _cache[1] || (_cache[1] = ($event: any) => ((filters).value = $event)),
          "shows-archived-activities": filters.value.includesInactive,
          "onUpdate:showsArchivedActivities": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.includesInactive) = $event)),
          "shows-only-important": filters.value.onlyImportant,
          "onUpdate:showsOnlyImportant": _cache[3] || (_cache[3] = ($event: any) => ((filters.value.onlyImportant) = $event)),
          "shows-only-non-assigned": filters.value.onlyNonAssigned,
          "onUpdate:showsOnlyNonAssigned": _cache[4] || (_cache[4] = ($event: any) => ((filters.value.onlyNonAssigned) = $event)),
          activities: activities.value,
          statuses: allActivityStatuses.value,
          "column-statuses": activityStatuses.value,
          "dev-statuses": activityDevStatuses.value,
          projects: projects.value,
          users: employees.value,
          "activity-details-users": 
        selectedActivityUsers.value.length > 0 ? selectedActivityUsers.value : employees.value
      ,
          "user-id": user.value?.id,
          "is-editable": isUserEmployee.value ? true : 'only-own',
          "is-task-checkable": isTaskCheckable,
          "selected-activity": activity.value,
          "attachment-enabled": true,
          "load-attachment-preview": loadAttachmentPreview,
          "time-tracking-data": timeTrackingData.value,
          "feed-mode": __props.feedMode,
          sprints: sprints.value,
          "activity-sprints": activitySprints.value,
          "sprint-planning-data": sprintPlanningData.value,
          milestones: milestones.value,
          history: activityHistory.value,
          "history-loading": _unref(activityHistoryLoading),
          onSelectActivity: selectActivity,
          onFetchHistory: fetchHistory,
          onAddActivity: handleCreateActivity,
          onStartTimer: emitStartTimer,
          "onUpdate:activity": handleUpdateActivity,
          "onClear:searchText": _cache[5] || (_cache[5] = ($event: any) => (filters.value.searchTerm = '')),
          onAddSubtask: _unref(createTask),
          onUpdateSubtask: _unref(updateTask),
          onDeleteSubtask: _unref(deleteTask),
          onAddComment: handleCreateComment,
          onUpdateComment: handleUpdateComment,
          onRemoveComment: _unref(deleteComment),
          onArchiveActivity: _unref(archiveActivity),
          onUnarchiveActivity: _unref(unarchiveActivity),
          onAddFiles: addFiles,
          onRemoveFile: removeFile,
          onToast: showToast,
          onCreateSprint: handleCreateSprint
        }, null, 8, ["search-text", "filters", "shows-archived-activities", "shows-only-important", "shows-only-non-assigned", "activities", "statuses", "column-statuses", "dev-statuses", "projects", "users", "activity-details-users", "user-id", "is-editable", "selected-activity", "time-tracking-data", "feed-mode", "sprints", "activity-sprints", "sprint-planning-data", "milestones", "history", "history-loading", "onAddSubtask", "onUpdateSubtask", "onDeleteSubtask", "onRemoveComment", "onArchiveActivity", "onUnarchiveActivity"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})